/**
 * Resource Form Component
 * 
 * Beautiful modal form for creating and editing resources.
 * Supports multiple file types with drag-and-drop upload.
 * 
 * @module ResourceForm
 */

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Video,
  Image,
  Link as LinkIcon,
  Upload,
  X,
  Save,
  Eye,
  Sparkles,
  Tag,
  AlertCircle
} from 'lucide-react';
import { InlineLoading } from '../common/LoadingStates';
import { RichTextEditor } from '../common/RichTextEditor';
import { normalizeDescriptionContent, stripHtml } from '../../utils/formatText';
import { Resource, CreateResourceData, ResourceCategory, ResourceType, ResourceStatus, ResourceVisibility } from '../../@types/resource';
import { ResourceService } from '../../services/ResourceService';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';

/**
 * Props interface
 */
export interface ResourceFormProps {
  resource?: Resource;
  mode: 'create' | 'edit';
  isLoading?: boolean;
  onSubmit: (data: CreateResourceData, files: File[]) => Promise<void>;
  onCancel: () => void;
}

/**
 * Styled Components
 */
const FormOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: ${Spacing.lg};
  animation: fadeIn 0.2s ease;
  overflow: auto;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const FormContainer = styled.div`
  background: white;
  border-radius: ${BorderRadius.xl};
  box-shadow: ${Shadows.xl};
  width: 100%;
  max-width: 960px;
  max-height: calc(100vh - ${Spacing.xxl});
  overflow-y: auto;
  animation: slideUp 0.3s ease;
  scrollbar-width: thin;

  @media (max-width: 768px) {
    max-width: 95vw;
    max-height: calc(100vh - ${Spacing.lg});
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.xl};
  border-bottom: 1px solid ${ThemeColors.border.light};
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const FormTitle = styled.h2`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};

  svg {
    color: ${ThemeColors.primary.main};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: ${Spacing.sm};
  cursor: pointer;
  color: ${ThemeColors.text.secondary};
  border-radius: ${BorderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${ThemeColors.background.secondary};
    color: ${ThemeColors.text.primary};
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

const FormContent = styled.div`
  padding: ${Spacing.lg} ${Spacing.xl};
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${Spacing.lg};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};

  svg {
    width: 16px;
    height: 16px;
    color: ${ThemeColors.primary.main};
  }
`;

const Required = styled.span`
  color: ${ThemeColors.error.main};
  margin-left: ${Spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: ${Spacing.md};
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${Spacing.md};
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${Spacing.md};
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${Spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FileUploadArea = styled.div<{ isDragging?: boolean }>`
  border: 2px dashed ${props => props.isDragging ? ThemeColors.primary.main : ThemeColors.border.main};
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.xxl};
  text-align: center;
  background: ${props => props.isDragging ? ThemeColors.primary.light : ThemeColors.background.secondary};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light};
  }
`;

const UploadIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto ${Spacing.md};
  background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  svg {
    width: 30px;
    height: 30px;
  }
`;

const UploadText = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.primary};
  font-weight: ${Typography.fontWeight.medium};
  margin: 0 0 ${Spacing.xs};
`;

const UploadSubtext = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

const FileList = styled.div`
  margin-top: ${Spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.sm} ${Spacing.md};
  background: ${ThemeColors.background.secondary};
  border-radius: ${BorderRadius.sm};
  border: 1px solid ${ThemeColors.border.light};
`;

const FileName = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const FileSize = styled.span`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  margin-left: ${Spacing.sm};
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  padding: ${Spacing.xs};
  cursor: pointer;
  color: ${ThemeColors.error.main};
  border-radius: ${BorderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${ThemeColors.error.light};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TagInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm};
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  min-height: 44px;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const TagChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background: ${ThemeColors.primary.light};
  color: ${ThemeColors.primary.dark};
  border-radius: 16px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};

  button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: ${ThemeColors.primary.dark};
    display: flex;
    align-items: center;

    &:hover {
      color: ${ThemeColors.error.main};
    }

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const TagInputField = styled.input`
  border: none;
  outline: none;
  flex: 1;
  min-width: 100px;
  font-size: ${Typography.fontSize.sm};
  padding: ${Spacing.xs};

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  padding: ${Spacing.md};
  background: ${ThemeColors.background.secondary};
  border-radius: ${BorderRadius.md};
  border: 1px solid ${ThemeColors.border.light};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  cursor: pointer;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: ${ThemeColors.primary.main};
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${ThemeColors.warning.main};
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${Spacing.md};
  padding: ${Spacing.lg} ${Spacing.xl};
  border-top: 1px solid ${ThemeColors.border.light};
  position: sticky;
  bottom: 0;
  background: white;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.md} ${Spacing.xl};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
    color: white;
    box-shadow: ${Shadows.md};
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: ${Shadows.lg};
    }
    
    &:active {
      transform: translateY(0);
    }
    
    &:disabled {
      background: ${ThemeColors.gray[400]};
      cursor: not-allowed;
      transform: none;
    }
  ` : `
    background: white;
    color: ${ThemeColors.text.primary};
    border: 2px solid ${ThemeColors.border.main};
    
    &:hover:not(:disabled) {
      background: ${ThemeColors.background.secondary};
      border-color: ${ThemeColors.primary.main};
    }
  `}

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  color: ${ThemeColors.error.main};
  font-size: ${Typography.fontSize.sm};
  margin-top: ${Spacing.xs};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

/**
 * Format file size
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * ResourceForm Component
 */
export const ResourceForm: React.FC<ResourceFormProps> = ({
  resource,
  mode,
  isLoading = false,
  onSubmit,
  onCancel
}) => {
  const { t } = useTranslation();
  console.log('ResourceForm component rendered with mode:', mode, 'isLoading:', isLoading);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Form state
  const [formData, setFormData] = useState<CreateResourceData>({
    title: '',
    description: '',
    category: 'training',
    type: 'document',
    status: 'published',
    visibility: 'all',
    textContent: '',
    linkUrl: '',
    tags: [],
    featured: false
  });

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tagInput, setTagInput] = useState('');

  // Initialize form data for edit mode
  useEffect(() => {
    if (resource && mode === 'edit') {
      setFormData({
        title: resource.title,
        description: resource.description,
        category: resource.category,
        type: resource.type,
        status: resource.status,
        visibility: resource.visibility,
        textContent: resource.textContent || '',
        linkUrl: resource.linkUrl || '',
        tags: resource.tags || [],
        featured: resource.featured || false
      });
    }
  }, [resource, mode]);

  /**
   * Handle input changes
   */
  const handleChange = (field: keyof CreateResourceData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  /**
   * Handle drag over
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  /**
   * Handle drag leave
   */
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  /**
   * Handle drop
   */
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  /**
   * Remove file
   */
  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  /**
   * Handle tag input
   */
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags?.includes(tagInput.trim())) {
        handleChange('tags', [...(formData.tags || []), tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  /**
   * Remove tag
   */
  const removeTag = (tag: string) => {
    handleChange('tags', formData.tags?.filter(t => t !== tag) || []);
  };

  /**
   * Validate form
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!stripHtml(formData.description)) {
      newErrors.description = 'Description is required';
    }

    if (formData.type === 'link' && !formData.linkUrl?.trim()) {
      newErrors.linkUrl = 'URL is required for link type resources';
    }

    if (mode === 'create' && formData.type !== 'text' && formData.type !== 'link' && selectedFiles.length === 0) {
      newErrors.files = 'Please upload at least one file';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle submit
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(
        {
          ...formData,
          description: normalizeDescriptionContent(formData.description),
        },
        selectedFiles
      );
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  /**
   * Handle overlay click
   */
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  /**
   * Get type icon
   */
  const getTypeIcon = (type: ResourceType) => {
    switch (type) {
      case 'document': return <FileText />;
      case 'video': return <Video />;
      case 'photo': return <Image />;
      case 'link': return <LinkIcon />;
      case 'text': return <FileText />;
      default: return <FileText />;
    }
  };

  console.log('ResourceForm returning JSX');
  return (
    <FormOverlay onClick={handleOverlayClick}>
      <FormContainer>
        <FormHeader>
          <FormTitle>
            {getTypeIcon(formData.type)}
            {mode === 'create' ? 'Create New Resource' : 'Edit Resource'}
          </FormTitle>
          <CloseButton onClick={onCancel} type="button">
            <X />
          </CloseButton>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormContent>
            <FormColumn>
              {/* Title */}
              <FormGroup>
                <Label>
                  Resource Title <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder={t('resourceForm.enterTitle')}
                  disabled={isLoading}
                />
                {errors.title && (
                  <ErrorMessage>
                    <AlertCircle />
                    {errors.title}
                  </ErrorMessage>
                )}
              </FormGroup>

              {/* Category and Type */}
              <FormGrid>
                <FormGroup>
                  <Label>
                    Category <Required>*</Required>
                  </Label>
                  <Select
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value as ResourceCategory)}
                    disabled={isLoading}
                  >
                    {ResourceService.CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>
                    Resource Type <Required>*</Required>
                  </Label>
                  <Select
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value as ResourceType)}
                    disabled={isLoading}
                  >
                    <option value="document">📄 Document</option>
                    <option value="video">🎥 Video</option>
                    <option value="photo">🖼️ Photo</option>
                    <option value="text">📝 Text</option>
                    <option value="link">🔗 Link</option>
                  </Select>
                </FormGroup>
              </FormGrid>

              {/* Description */}
              <FormGroup>
                <Label>
                  Description <Required>*</Required>
                </Label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => handleChange('description', value)}
                  placeholder="Provide a detailed description of this resource..."
                  disabled={isLoading}
                  hasError={!!errors.description}
                />
                {errors.description && (
                  <ErrorMessage>
                    <AlertCircle />
                    {errors.description}
                  </ErrorMessage>
                )}
              </FormGroup>

              {/* Featured */}
              <FormGroup>
                <CheckboxGroup>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => handleChange('featured', e.target.checked)}
                      disabled={isLoading}
                    />
                    <Sparkles />
                    Feature this resource (appears at the top)
                  </CheckboxLabel>
                </CheckboxGroup>
              </FormGroup>

              {/* Text Content (for text type) */}
              {formData.type === 'text' && (
                <FormGroup>
                  <Label>
                    Text Content
                  </Label>
                  <TextArea
                    value={formData.textContent}
                    onChange={(e) => handleChange('textContent', e.target.value)}
                    placeholder="Enter the text content..."
                    disabled={isLoading}
                    style={{ minHeight: '200px' }}
                  />
                </FormGroup>
              )}

            </FormColumn>

            <FormColumn>

            {/* Link URL (for link type) */}
              {formData.type === 'link' && (
                <FormGroup>
                  <Label>
                    Link URL <Required>*</Required>
                  </Label>
                  <Input
                    type="url"
                    value={formData.linkUrl}
                    onChange={(e) => handleChange('linkUrl', e.target.value)}
                    placeholder="https://example.com"
                    disabled={isLoading}
                  />
                  {errors.linkUrl && (
                    <ErrorMessage>
                      <AlertCircle />
                      {errors.linkUrl}
                    </ErrorMessage>
                  )}
                </FormGroup>
              )}

              {/* File Upload (for document, video, photo types) */}
              {formData.type !== 'text' && formData.type !== 'link' && (
                <FormGroup>
                  <Label>
                    {formData.type === 'document' && 'Upload Documents'}
                    {formData.type === 'video' && 'Upload Videos'}
                    {formData.type === 'photo' && 'Upload Photos'}
                    {mode === 'create' && <Required>*</Required>}
                  </Label>
                  <FileUploadArea
                    isDragging={isDragging}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <UploadIcon>
                      <Upload />
                    </UploadIcon>
                    <UploadText>
                      Drag & drop files here or click to browse
                    </UploadText>
                    <UploadSubtext>
                      {formData.type === 'document' && 'Supports: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX'}
                      {formData.type === 'video' && 'Supports: MP4, MOV, AVI, WMV'}
                      {formData.type === 'photo' && 'Supports: JPG, PNG, GIF, WEBP'}
                    </UploadSubtext>
                  </FileUploadArea>
                  <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={
                      formData.type === 'document' ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx' :
                      formData.type === 'video' ? '.mp4,.mov,.avi,.wmv' :
                      formData.type === 'photo' ? '.jpg,.jpeg,.png,.gif,.webp' :
                      '*'
                    }
                    onChange={handleFileSelect}
                  />
                  {selectedFiles.length > 0 && (
                    <FileList>
                      {selectedFiles.map((file, index) => (
                        <FileItem key={index}>
                          <FileName>
                            {getTypeIcon(formData.type)}
                            {file.name}
                            <FileSize>{formatFileSize(file.size)}</FileSize>
                          </FileName>
                          <RemoveFileButton onClick={() => removeFile(index)} type="button">
                            <X />
                          </RemoveFileButton>
                        </FileItem>
                      ))}
                    </FileList>
                  )}
                  {errors.files && (
                    <ErrorMessage>
                      <AlertCircle />
                      {errors.files}
                    </ErrorMessage>
                  )}
                </FormGroup>
              )}

              {/* Tags */}
              <FormGroup>
                <Label>
                  <Tag />
                  Tags
                </Label>
                <TagInput>
                  {formData.tags?.map(tag => (
                    <TagChip key={tag}>
                      {tag}
                      <button onClick={() => removeTag(tag)} type="button">
                        <X />
                      </button>
                    </TagChip>
                  ))}
                  <TagInputField
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder={t('resourceForm.addTags')}
                    disabled={isLoading}
                  />
                </TagInput>
              </FormGroup>

              {/* Status and Visibility */}
              <FormGrid>
                <FormGroup>
                  <Label>
                    Status
                  </Label>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value as ResourceStatus)}
                    disabled={isLoading}
                  >
                    <option value="published">✅ Published (Visible to users)</option>
                    <option value="draft">📝 Draft (Not visible)</option>
                    <option value="archived">🗄️ Archived</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>
                    <Eye />
                    Visibility
                  </Label>
                  <Select
                    value={formData.visibility}
                    onChange={(e) => handleChange('visibility', e.target.value as ResourceVisibility)}
                    disabled={isLoading}
                  >
                    <option value="all">👥 All Users</option>
                    <option value="sales-reps-only">💼 Sales Reps Only</option>
                    <option value="managers-only">👔 Managers Only</option>
                  </Select>
                </FormGroup>
              </FormGrid>

            </FormColumn>
          </FormContent>

          <FormActions>
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              <X />
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <InlineLoading size="18px" />
                  {mode === 'create'
                    ? t('resources.creatingResource', 'Creating...')
                    : t('resources.savingResource', 'Saving...')}
                </>
              ) : (
                <>
                  <Save />
                  {mode === 'create'
                    ? t('resources.createResource', 'Create Resource')
                    : t('resources.saveChanges', 'Save Changes')}
                </>
              )}
            </Button>
          </FormActions>
        </form>
      </FormContainer>
    </FormOverlay>
  );
};

